import BaseComponent from '../base-component/base-component'
import { checkEnterKey } from '../../utils/utils'
import { debounce } from '../../utils/debounce'
import Carousel from './carousel'
import { Nullable } from '../../types/common'
import CarouselDots from './carousel-dots'

class CarouselBtn extends BaseComponent {
  constructor() {
    super()
  }

  connectedCallback(): void {
    if (this.clientWidth === 0) {
      return
    }

    this.update()

    if (this.carousel) {
      this.carousel.embla.on('reInit', this.handleCarouselReInit)
      this.carousel.embla.on('select', this.handleCarouselSelect)
      this.carousel.embla.on('scroll', this.handleCarouselScroll)
    }

    this.addEventListener('keydown', this.handleKeyDown)
    this.addEventListener('click', this.handleButtonClick)
  }

  disconnectedCallback(): void {
    if (this.clientWidth === 0) {
      return
    }

    if (this.carousel) {
      this.carousel.embla.off('reInit', this.handleCarouselReInit)
      this.carousel.embla.off('select', this.handleCarouselSelect)
      this.carousel.embla.off('scroll', this.handleCarouselScroll)
    }

    this.removeEventListener('keydown', this.handleKeyDown)
    this.removeEventListener('click', this.handleButtonClick)
  }

  handleButtonClick = (): void => {
    this.trigger()
  }

  handleCarouselReInit = (): void => {
    this.update()
  }

  handleCarouselSelect = (): void => {
    this.update()
  }

  handleCarouselScroll = debounce((): void => {
    this.update()
  }, 100)

  handleKeyDown = (event: KeyboardEvent): void => {
    const isEnterKey = checkEnterKey(event.key || event.keyCode)

    if (isEnterKey) {
      event.preventDefault()

      this.trigger()
    }
  }

  update(): void {
    this.updateDisabled()
    this.updateVisible()
  }

  updateDisabled = (): void => {
    this.toggleAttribute('disabled', !this.canScroll())
  }

  updateVisible(): void {
    if (!this.carousel) {
      return
    }

    const snapList = [...Array(this.carousel.embla.slideNodes().length).keys()]
    const firstSnap = 0
    const lastSnap = snapList.length - 1
    const slidesInView = this.carousel.embla.slidesInView()

    const isNextButton = this.dataset.scrollDirection === 'next'

    const isVisible = isNextButton
      ? slidesInView.includes(lastSnap)
      : slidesInView.includes(firstSnap)

    this.toggleAttribute('has-not-slides-not-in-view', isVisible)
  }

  canScroll(): boolean {
    if (!this.carousel) {
      return false
    }

    const isScrollNext = this.dataset.scrollDirection === 'next'
    const canScroll = isScrollNext
      ? this.carousel.canScrollNext()
      : this.carousel.canScrollPrev()

    return canScroll
  }

  trigger(): void {
    if (!this.carousel) {
      return
    }

    const { scrollNext, scrollPrev } = this.carousel
    const isScrollNext = this.dataset.scrollDirection === 'next'

    if (isScrollNext) {
      scrollNext()
    } else {
      scrollPrev()
    }
  }

  get carousel() {
    const carouselDots = this.closest<CarouselDots>('carousel-dots')
    const carousel = this.closest<Carousel>('carousel-component')

    return carouselDots || carousel
  }
}

export default CarouselBtn
